// @ts-nocheck

import cn from 'classnames';
import { capitalize, cloneDeep, compact, groupBy, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import {
  Checkbox,
  FiltersV2,
  getInterpolateColorFromValue,
  InsightsChartItem,
  useDidUpdate,
  usePrevious
} from 'sonata-react-components';
import {
  BaseCurrencyEnum,
  BaseCurrencyPositionEnum,
  BaseCurrencySymbolEnum
} from 'sonata-static-storage/lib/enum/baseCurrency.enum';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { normalize } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { DEFAULT_COLOR_PALETTE_MAP } from 'sonata-static-storage/lib/samples/mapPreferences.samples';

import { formatValue, getMetricsKeys, getVariableFilterParsed } from '@/helpers/reporting.helpers';
import { getPaginatedInsightsStats } from '@/modules/reporting/reporting.resources';
import { reportingInsightsSetStats } from '@/store/actions/reporting.actions';
import { getReportingType, getStaticMode } from '@/store/selectors/reporting.selectors';
import {
  getCountry,
  getInsightCountry,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import CustomLegendGenderAge from '../CustomLegendGenderAge/CustomLegendGenderAge';
import {
  getComponent,
  getCountryFilter,
  getFilterConfigValue,
  getSortConfig,
  getUpdatedProps
} from '../reporting.helpers';
import { ReportingInsightsRankingItemRawSelectorStyled } from './ReportingInsightsRankingItemRawSelector.style';

const GROUP_KEY_BY_DCO_DIMENSION = {
  context: 'dco.contextId',
  scenario: 'dco.scenarioId'
};

const ageGroups = ['18-24', '25-34', '35-44', '45-54', '55-64', '>65'];
function ReportingInsightsRankingItem({
  calculateWeight,
  campaignInfo,
  chartConfig = {},
  chartType,
  country,
  currencyInfo,
  family,
  field,
  filterOptions,
  filters,
  groupsOptions: groupsOptionsByConfig,
  hideKpi = false,
  hideOthers,
  id,
  insightsDataBeingChecked = false,
  insightsDataCanBeLoaded = true,
  itemId,
  mapId,
  metricsKpis,
  onConfigChange,
  onFiltersChange,
  onRankingItemClick,
  rankingConfig,
  rawDataMode,
  reportOptions,
  section,
  showMoreQuantity,
  shownItems = 5
}) {
  const { formatMessage } = useIntl();
  const flexDirection = rankingConfig?.flexDirection || 'row';
  const intl = useIntl();
  const dispatch = useDispatch();
  const reportingStatsToken = {};

  const staticMode = useSelector(getStaticMode);
  const variableType = useSelector(store => getVariableType(store, section));
  const variableValues = useSelector(store => getVariableValues(store, section));
  const selectedCountry = useSelector(store =>
    ['insightsRankingItem_attrVids', 'insightsRankingItem_cartography'].includes(id)
      ? getInsightCountry(store, id)
      : getCountry(store, 'insights')
  );
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const isDooh = isDOOHCampaign(useSelector(getReportingType));
  const showMoreQuantityPermissions = checkUserResourcePermissions('reportingInsightsRankingShowMore', user, userRoles);

  const [
    { filledChartConfig, filledRankingConfig, filtersStatus, isRankingExpanded, isRawDataModeEnabled, otherCount },
    setState
  ] = useState({
    filledChartConfig: {},
    filledRankingConfig: [],
    filtersStatus: [],
    isRankingExpanded: false,
    isRawDataModeEnabled: rawDataMode?.defaultActive ?? false,
    otherCount: 0
  });

  const kpiRef = useRef();
  const metricRef = useRef();
  const showMoreQuantityRef = useRef(showMoreQuantity);
  const currentSize = shownItems > 5 ? shownItems : groupsOptionsByConfig?.[0].size || 5;

  const { kpi, metric } = metricsKpis;
  if (kpi) {
    kpiRef.current = {
      id: getMetricsKeys(metricsKpis.kpi.id),
      label: metricsKpis.kpi.label
    };
  }
  if (metric) {
    metricRef.current = {
      id: getMetricsKeys(metricsKpis.metric.id),
      label: metricsKpis.metric.label
    };
  }

  const prevMetricRef = usePrevious(metricRef.current);
  const prevKpiRef = usePrevious(kpiRef.current);
  const hasInsightsByChanged =
    prevMetricRef && (!isEqual(kpiRef.current?.id, prevKpiRef?.id) || !isEqual(metricRef.current.id, prevMetricRef.id));
  const filtersConfig = hasInsightsByChanged
    ? []
    : filtersStatus.map(filter => {
        if (filter.id.startsWith('switch') && filter.value) {
          return {
            key: 'groupOptions',
            value: groupsOptionsByConfig.map(groupOptionsEntry => ({
              ...groupOptionsEntry,
              groupBy: filters.find(f => f.id === filter.id)?.groupBy
            }))
          };
        }
        switch (filter.id) {
          case 'order':
            return {
              key: 'sortOptions',
              value: getSortConfig(filter.value.value)
            };
          case 'boundary':
            return {
              key: 'groupOptions',
              value: groupsOptionsByConfig.map(groupOptionsEntry => ({
                ...groupOptionsEntry,
                groupBy: `cartoPyramid.${filter.value?.value ? filter.value.value : filter.value}`
              }))
            };
          case 'content-category-provider':
            return {
              key: 'groupOptions',
              value: groupsOptionsByConfig.map(groupOptionsEntry => ({
                ...groupOptionsEntry,
                groupBy: filter.value.value
              }))
            };
          case 'dco-dimension-selector':
            return {
              key: 'groupOptions',
              value: groupsOptionsByConfig.map(groupOptionsEntry => ({
                ...groupOptionsEntry,
                groupBy: GROUP_KEY_BY_DCO_DIMENSION[filter.value.value]
              }))
            };
          default:
            return {};
        }
      });

  const rankingConfigBuilder = statsData => {
    const {
      metadata: { totalResults = {} },
      results = []
    } = statsData || {};
    const { colorByLabel = {}, colors = {}, useCustomProgressBarColor, ...rest } = rankingConfig;
    const { items, others } = colors;
    const filterConfigValue = getFilterConfigValue(filtersConfig, 'groupOptions');
    const key = Array.isArray(filterConfigValue) && filterConfigValue.length ? filterConfigValue[0].groupBy : field;
    const maximumResult = results.reduce(
      (prev, { [metricRef.current.id]: currentMetricValue = 0 }) =>
        prev < currentMetricValue ? currentMetricValue : prev,
      0
    );
    const totalMetricValue = metricRef?.current?.id ? totalResults[metricRef?.current?.id] : 1;
    const totalKpiValue = kpiRef.current?.id ? totalResults[kpiRef.current?.id] : 1;
    const maximumValue = isRawDataModeEnabled ? maximumResult : 100;
    const totalMetricValueFormatted = formatValue(totalMetricValue, metricRef.current.id, currencyInfo, 0, true);
    const totalKpiValueFormatted = normalize(totalKpiValue, 2, currencyInfo.countries[0]);
    return [
      {
        currentDisplayValue: totalMetricValueFormatted,
        currentKpiValue: totalKpiValue,
        currentMetricValue: totalMetricValue,
        label: intl.formatMessage(
          {
            defaultMessage: `Total ${metricRef.current.label}`,
            id: 'reporting.insights.total'
          },
          {
            metric: metricRef.current.label
          }
        ),
        rawMode: true,
        ...(!hideKpi && {
          tagConfig: {
            children: `${kpiRef.current?.label}: ${totalKpiValueFormatted}%`
          }
        }),
        showDotColor: false,
        showProgressBar: false
      },
      ...results.map((resultData, i) => {
        const kpiValue = resultData[kpiRef.current?.id] ?? 0;
        const metricValue = resultData[metricRef?.current?.id] ?? 0;
        const currentValue = isRawDataModeEnabled
          ? metricValue
          : Number(((metricValue / totalMetricValue) * 100).toFixed(2));
        const currentDisplayValue = formatValue(
          currentValue,
          metricRef.current.id,
          currencyInfo,
          isRawDataModeEnabled ? 0 : 2,
          isRawDataModeEnabled
        );
        const kpiValueFormatted = normalize(kpiValue, 2, currencyInfo.countries[0]);

        const label =
          resultData[key] === 'others'
            ? intl.formatMessage(
                {
                  defaultMessage: `Other (${otherCount})`,
                  id: 'reporting.insights.other'
                },
                { count: otherCount }
              )
            : isRankingExpanded
              ? `${i + 1} - ${resultData[key]}`
              : resultData[key];

        return {
          currentDisplayValue,
          currentKpiValue: kpiValue.toFixed(2),
          currentMetricValue: currentValue,
          label,
          maximumValue,
          rawMode: isRawDataModeEnabled,
          ...(useCustomProgressBarColor && {
            progressBarColor: getInterpolateColorFromValue(
              resultData.weights?.[metricRef?.current?.id] || 0,
              DEFAULT_COLOR_PALETTE_MAP
            )
          }),
          ...(!hideKpi && {
            tagConfig: {
              children: `${kpiRef.current?.label}: ${kpiValueFormatted}%`
            }
          }),
          ...rest,
          ...(!isEmpty(colorByLabel)
            ? {
                dotColor: colorByLabel[resultData[field].toLowerCase()]
              }
            : {
                dotColor: resultData[key] === 'others' ? others : items && items[i % items.length]
              }),
          ...(onRankingItemClick && {
            onClick: () =>
              onRankingItemClick({
                data: resultData,
                index: i,
                label
              })
          })
        };
      })
    ];
  };

  const barChartConfigBuilder = statsData => {
    const result = statsData.results ? statsData.results : [];
    const columnSeries = result.map(resultData => ({
      data: [resultData[metricRef.current.id]],
      name: resultData[field]
    }));
    return {
      series: columnSeries,
      type: chartType,
      ...chartConfig
    };
  };

  const pieChartConfigBuilder = statsData => {
    const { colorByLabel = {} } = chartConfig;
    const result = statsData.results ? statsData.results : [];
    const columnSeries = result.map(resultData => ({
      name: resultData[field],
      y: resultData[metricRef.current.id],
      ...(!isEmpty(colorByLabel) && { color: colorByLabel[resultData[field].toLowerCase()] })
    }));
    return {
      series: columnSeries,
      type: chartType,
      ...chartConfig
    };
  };

  const genderAgeChartConfigBuilder = (statsData, type) => {
    const { results } = statsData;
    const { colorByLabel = {} } = chartConfig;
    const xAxisCategories = [];
    const series = compact(
      ['Male', 'Female'].map(gender => {
        const ageData = ageGroups
          .map(ageGroup => {
            const genderAndAgeResults = results.find(resultsEntry => {
              const isGenderAgeColumn =
                resultsEntry.userGender?.toLowerCase() === gender.toLowerCase() &&
                resultsEntry.userAgeRanges?.toLowerCase() === ageGroup.toLowerCase();
              return type === 'genderAgeColumn'
                ? isGenderAgeColumn
                : resultsEntry.demographicGenderAge === `${gender} ${ageGroup}`;
            });
            const value = genderAndAgeResults ? genderAndAgeResults[metricRef.current.id] || 0 : 0;
            value && xAxisCategories.push(ageGroup.toLowerCase());
            return value;
          })
          .filter(el => el > 0);
        if (!ageData.reduce((acc, entry) => acc + entry, 0)) {
          return null;
        }
        return {
          data: ageData,
          name: intl.formatMessage({
            defaultMessage: capitalize(gender),
            id: `reporting.ageAndGender.${gender}`
          }),
          ...(!isEmpty(colorByLabel) && { color: colorByLabel[gender.toLowerCase()] })
        };
      })
    );
    return {
      height: 200,
      series,
      type: 'column',
      xAxisCategories,
      ...chartConfig
    };
  };

  const treemapConfigBuilder = statsData => {
    const { metadata = {}, results = [] } = statsData || {};
    const filterConfigValue = getFilterConfigValue(filtersConfig, 'groupOptions');
    const key = Array.isArray(filterConfigValue) && filterConfigValue.length ? filterConfigValue[0].groupBy : field;
    const topResults = results.slice(0, currentSize);
    const seriesResult = !hideOthers
      ? topResults
      : topResults.filter(result => !Object.values(result).some(val => val === 'others'));
    const kpiValues = seriesResult.map(a => a[kpiRef.current?.id] ?? 0);
    const minKPI = Math.min(...kpiValues);
    const maxKPI = Math.max(...kpiValues);
    const totalMetricValue = seriesResult.reduce((a, b) => a + b[metricRef.current.id], 0);
    return {
      legend: true,
      series: [
        {
          data: seriesResult.map(seriesResultData => {
            const { [kpiRef.current?.id]: kpiValue = 0, [metricRef.current.id]: metricValue = 0 } = seriesResultData;
            const currentValue = Number(((metricValue / totalMetricValue) * 100).toFixed(2));
            const currentDisplayValue = normalize(metricValue, 2, currencyInfo.countries[0]);
            const kpiValueFormatted = normalize(kpiValue, 2, currencyInfo.countries[0]);
            const kpiValueFormattedText = `${kpiValueFormatted}%`;
            const totalResults = metadata?.totalResults?.[metricRef?.current?.id];
            const name = isDooh ? `${((metricValue / totalResults) * 100).toFixed(2)}%` : kpiValueFormattedText;
            return {
              colorValue: kpiValue,
              name,
              tooltipName: seriesResultData[key],
              value: currentValue,
              y: currentValue,
              [metricRef.current.id]: currentDisplayValue,
              ...(!isDooh ? { [kpiRef.current?.id]: kpiValueFormattedText } : {})
            };
          }),
          layoutAlgorithm: 'squarified'
        }
      ],
      tooltipFormatter: point => `
        <div class="treemap-tooltip">
          <span class="treemap-tooltip__header">
            ${point.tooltipName}
          </span>
          <span class="treemap-tooltip__metric">
            ${capitalize(metricRef.current.id)}: ${point[metricRef.current.id]}
          </span>
          <span class="${cn('treemap-tooltip__kpi', { 'treemap-tooltip__kpi--hide': isDooh })}">
            ${kpiRef.current?.id?.toUpperCase()}: ${point[kpiRef.current?.id]}
          </span>
        </div>
      `,
      type: 'treemap',
      useTooltipHTML: true,
      ...chartConfig,
      colorAxis: {
        ...chartConfig.colorAxis,
        max: maxKPI,
        min: minKPI === maxKPI ? 0 : minKPI
      }
    };
  };

  const chartConfigBuilder = statsData => {
    switch (chartType) {
      case 'stackedBar':
      case 'stackedColumn':
        return barChartConfigBuilder(statsData);
      case 'genderAgeColumn':
      case 'DOOHGenderAgeColumn':
        return genderAgeChartConfigBuilder(statsData, chartType);
      case 'treemap':
        return treemapConfigBuilder(statsData);
      default:
        return pieChartConfigBuilder(statsData);
    }
  };

  const getDefaultSortValue = filter => {
    if (filter) {
      const {
        props: { defaultOrder }
      } = filter;
      if (!isEmpty(defaultOrder)) {
        return {
          metric: metricsKpis[defaultOrder.type].id,
          order: defaultOrder.order
        };
      }
      return null;
    }
    return null;
  };

  const getParams = () => {
    const { cIds, cartographyFilter, currencyOptions, dateInterval, userId } = campaignInfo;
    const sortFilter = filters.find(filter => filter.id === 'order');
    const defaultSort = getDefaultSortValue(sortFilter);
    const sortOptions = getFilterConfigValue(filtersConfig, 'sortOptions', defaultSort);
    const filterGroupOptions = getFilterConfigValue(filtersConfig, 'groupOptions');
    const groupOptions = isEmpty(filterGroupOptions) ? groupsOptionsByConfig : filterGroupOptions;
    const groupSize = isRankingExpanded ? showMoreQuantity + currentSize : currentSize;
    const variableFilter = variableValues.length ? getVariableFilterParsed(variableType, variableValues) : {};
    const countryFilter = getCountryFilter(selectedCountry);

    return {
      calculateWeight: calculateWeight || false,
      campaignIds: cIds,
      cartography: cartographyFilter,
      dateInterval,
      filterOptions: {
        ...filterOptions,
        ...variableFilter,
        ...countryFilter
      },
      groupOptions: groupOptions.map(gOptions => ({
        ...gOptions,
        size: groupSize
      })),
      outputKpi: !isDooh && kpiRef.current?.id,
      outputMetric: metricRef?.current?.id,
      reportOptions: {
        ...(reportOptions || {}),
        currencyOptions
      },
      requestToken: reportingStatsToken,
      sortOptions,
      userId
    };
  };

  const params = getParams();
  const filterGroupOptions = params.groupOptions;
  const groupOptions = isEmpty(filterGroupOptions) ? groupsOptionsByConfig : filterGroupOptions;
  const cacheKey = JSON.stringify(params);
  const {
    data,
    error,
    isError,
    isLoading: loading,
    refetch,
    status
  } = useQuery(
    cacheKey,
    async () => {
      setState(prevState => ({
        ...prevState,
        filledRankingConfig: []
      }));
      return getPaginatedInsightsStats(
        params.userId,
        params.campaignIds,
        params.dateInterval,
        params.cartography,
        params.outputKpi,
        params.outputMetric,
        params.groupOptions,
        params.sortOptions,
        params.reportOptions,
        params.filterOptions,
        params.calculateWeight,
        params.requestToken
      );
    },
    {
      enabled: !(isEmpty(groupOptions) || groupOptions.some(groupOption => !groupOption.groupBy))
    }
  );

  const getStatsStatus = () => {
    if (staticMode && error) {
      return 'notFound';
    }
    if (insightsDataCanBeLoaded) {
      switch (status) {
        case 'loading':
          return 'loading';
        case 'success': {
          const { totalSize } = data?.metadata?.groupOptions?.[0] || {
            size: 0,
            totalSize: 0
          };

          return totalSize > 0 ? 'ready' : 'empty';
        }
        case 'error':
          return 'error';
        default:
          return;
      }
    }
    return 'notApply';
  };

  const statsStatus = getStatsStatus();

  const prevData = usePrevious(data);
  useEffect(() => {
    if (status === 'success' && !isEqual(data, prevData)) {
      dispatch(reportingInsightsSetStats(id, data));
      const { size, totalSize: total } = data.metadata?.groupOptions?.[0] || {
        size: 0,
        totalSize: 0
      };
      const newOtherCount = total - size;
      showMoreQuantityRef.current = Math.min(showMoreQuantity, newOtherCount);

      setState(prevState => ({
        ...prevState,
        otherCount: newOtherCount
      }));
    }
  }, [data, prevData, status]);

  const reportingMap = useReportingMap();
  const mapData = reportingMap?.getMapData({ id: mapId });
  const prevMapData = usePrevious(mapData);
  const shouldReloadRanking = prevMapData && !isEqual(prevMapData, mapData);

  useEffect(() => {
    if (data) {
      const newRankingConfig = rankingConfig ? rankingConfigBuilder(data) : [];
      const newChartConfig = chartType
        ? {
            ...chartConfigBuilder(data),
            country
          }
        : {};
      if (!newChartConfig.colors && rankingConfig && rankingConfig.colors) {
        const hasOthers = newChartConfig.series.find(item => item.name === 'others');
        newChartConfig.colors = rankingConfig.colors.items.slice(
          0,
          hasOthers ? newChartConfig.series.length - 1 : newChartConfig.series.length
        );
        if (hasOthers) {
          newChartConfig.colors.push(rankingConfig.colors.others);
        }
      }

      setState(prevState => ({
        ...prevState,
        filledChartConfig: newChartConfig,
        filledRankingConfig: newRankingConfig
      }));
    }
  }, [chartType, data, isRawDataModeEnabled, otherCount, rankingConfig, shouldReloadRanking]);

  useDidUpdate(() => {
    if (isError && !isEmpty(filledRankingConfig)) {
      setState(prevState => ({
        ...prevState,
        filledRankingConfig: []
      }));
    }
  }, [isError, filledRankingConfig]);

  useEffect(() => {
    if (rawDataMode && typeof rawDataMode.force === 'boolean') {
      setState(prevState => ({
        ...prevState,
        isRawDataModeEnabled: rawDataMode.force
      }));
    }
  }, [rawDataMode]);

  useDidUpdate(() => {
    if (onConfigChange) {
      const sortFilter = filters.find(filter => filter.id === 'order');
      const defaultSort = getDefaultSortValue(sortFilter);
      const sortOptions = getFilterConfigValue(filtersConfig, 'sortOptions', defaultSort);
      const filterGroupOptions = getFilterConfigValue(filtersConfig, 'groupOptions');
      const groupOptions = isEmpty(filterGroupOptions) ? groupsOptionsByConfig : filterGroupOptions;
      const groupSize = isRankingExpanded ? showMoreQuantity + currentSize : currentSize;
      const additionalData =
        filtersStatus.find(filter =>
          ['boundary', 'content-category-provider', 'dco-dimension-selector'].includes(filter.id)
        )?.value?.label || 'N/A';

      onConfigChange({
        additionalData,
        filters: {
          ...filterOptions,
          ...(variableValues.length && { [variableType]: getVariableFilterParsed(variableType, variableValues) }),
          ...getCountryFilter(selectedCountry)
        },
        groupOptions: groupOptions.map(groupOption => ({
          ...groupOption,
          size: groupSize
        })),
        sortOptions
      });
    }
  }, [
    currentSize,
    filtersStatus,
    isRankingExpanded,
    showMoreQuantityRef,
    onConfigChange,
    selectedCountry,
    variableType,
    variableValues
  ]);

  const groupsFilter = groupBy(filters, 'type');
  const isMultiple = Object.values(groupsFilter).some(gF => gF.length > 1);

  const filtersClassName = cn('reportingInsightsItem__filters', {
    [`reportingInsightsItem__filters--${flexDirection}`]: filters && filters.length > 1
  });

  const filtersGroupsClassName = cn('reportingInsightsItem__filters-item');

  const updateFilters = newValues => {
    let valuesToUpdate = cloneDeep(newValues);
    const prevCheckboxSelected = filtersStatus.find(f => f.id.startsWith('switch') && f.value);
    const currentCheckboxesSelected = newValues.filter(f => f.id.startsWith('switch') && f.value);
    if (
      prevCheckboxSelected &&
      currentCheckboxesSelected.length &&
      currentCheckboxesSelected.some(f => f.id !== prevCheckboxSelected.id)
    ) {
      valuesToUpdate = valuesToUpdate.map(filter => {
        if (filter.id === prevCheckboxSelected.id) {
          return {
            ...filter,
            value: false
          };
        }
        return filter;
      });
    }
    if (onFiltersChange) onFiltersChange(valuesToUpdate);
    setState(currentState => ({
      ...currentState,
      filtersStatus: valuesToUpdate
    }));
  };

  const onRankingExpandedHandler = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isRankingExpanded: !prevState.isRankingExpanded
    }));
  }, [isRankingExpanded]);

  const totalMetricRowCount = 1;
  const showMoreConfig = {
    expanded: isRankingExpanded && filledRankingConfig.length > currentSize + totalMetricRowCount,
    quantity: showMoreQuantityRef.current,
    showMoreToggle: onRankingExpandedHandler
  };

  const filterKey = `filters-${itemId}${kpi ? `-${kpi.id}` : ''}${metric ? `-${metric.id}` : ''}`;

  const getFilterItem = ({ group = [] }) =>
    group.map(({ id: filterId, props, type }, key) => {
      const Component = getComponent(type);
      const updatedProps = getUpdatedProps(type, props, intl, id);
      const filterValue = filtersStatus.find(f => f.id === filterId)?.value;
      return (
        <FiltersV2.Filter className={filtersGroupsClassName} id={filterId} key={`${filterId}-${key}`}>
          <Component {...updatedProps} {...(type === 'checkbox' && filterValue != null && { active: filterValue })} />
        </FiltersV2.Filter>
      );
    });

  const handleRawDataModeChange = () => {
    const newValue = !isRawDataModeEnabled;
    if (rawDataMode.onChange) {
      rawDataMode.onChange(newValue);
    }
    setState(prevState => ({
      ...prevState,
      isRawDataModeEnabled: newValue
    }));
  };

  const emptyStateConfig = (() => {
    if (['empty', 'notApply'].includes(statsStatus)) {
      if (statsStatus === 'empty') {
        return {
          description: intl.formatMessage({
            defaultMessage: 'Please set different filters to view these insights',
            id: 'reporting.insights.emptyState.description'
          }),
          title: intl.formatMessage({
            defaultMessage: 'These settings are not available for the selected KPI',
            id: 'reporting.insights.emptyState.title'
          })
        };
      }
      return {
        description: intl.formatMessage({
          defaultMessage: 'Too see this data, deselect "Playouts" in insights settings',
          id: 'reporting.insights.notApply.description'
        }),
        title: intl.formatMessage({
          defaultMessage: 'These settings are not available for the selected KPI',
          id: 'reporting.insights.notApply.title'
        })
      };
    }
  })();

  return (
    <>
      {rawDataMode && !rawDataMode.hideCheckbox && (
        <ReportingInsightsRankingItemRawSelectorStyled>
          <Checkbox
            active={isRawDataModeEnabled}
            disabled={rawDataMode.disabled}
            id={`${id}_rawDataModeSelector`}
            isSwitch
            labelText={formatMessage({
              defaultMessage: 'View raw data',
              id: 'reporting.insights.rawDataSelector'
            })}
            onClickAction={handleRawDataModeChange}
          />
        </ReportingInsightsRankingItemRawSelectorStyled>
      )}
      <div className="reportingInsightsRankingItem__insightChartItemWrapper">
        <InsightsChartItem
          chartConfig={filledChartConfig}
          className={chartType}
          customLegend={
            ['genderAgeColumn', 'DOOHGenderAgeColumn'].includes(chartType) ? (
              <CustomLegendGenderAge metric={metricRef.current.label} />
            ) : null
          }
          empty={['empty', 'notApply'].includes(statsStatus)}
          emptyConfig={emptyStateConfig}
          error={isError}
          errorConfig={{
            buttonProps: {
              icon: 'fas fa-sync-alt',
              iconPosition: 'left',
              text: intl.formatMessage({
                defaultMessage: 'Refresh',
                id: 'reporting.insights.reloadState.reloadButton'
              })
            },
            description: intl.formatMessage({
              defaultMessage: 'Please refresh the page',
              id: 'reporting.insights.reloadState.description'
            }),
            title: intl.formatMessage({
              defaultMessage: 'There was an error loading these insights',
              id: 'reporting.insights.reloadState.title'
            })
          }}
          id={`${id}__chartItem`}
          itemsToDisplay={currentSize}
          loading={loading || insightsDataBeingChecked}
          noDataFound={staticMode && isError}
          omitShowMore={staticMode}
          rankingConfig={!['genderAgeColumn', 'DOOHGenderAgeColumn'].includes(chartType) ? filledRankingConfig : null}
          showMoreConfig={showMoreQuantityPermissions && showMoreConfig}
          type={isEmpty(filledChartConfig) ? 'ranking' : 'chart'}
          {...(!staticMode && {
            reloadCallback: refetch
          })}
        >
          {filters && filters.length && (
            <FiltersV2 className={filtersClassName} key={filterKey} onFiltersStatus={updateFilters}>
              {Object.entries(groupsFilter).map(([, group], index) => {
                if (isMultiple) {
                  return (
                    <div
                      className="reportingInsightsItem__filters-groups"
                      key={`groups_${family}_${field}_${id}_${group.id}_${index}`}
                    >
                      {getFilterItem({ group })}
                    </div>
                  );
                }
                return getFilterItem({ group });
              })}
            </FiltersV2>
          )}
        </InsightsChartItem>
      </div>
    </>
  );
}
ReportingInsightsRankingItem.propTypes = {
  campaignInfo: PropTypes.shape({
    cIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    cartographyFilter: PropTypes.object,
    currencyOptions: PropTypes.shape({
      baseCurrency: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired
    }),
    dateInterval: PropTypes.shape({
      beginDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      timezone: PropTypes.string.isRequired
    }),
    userId: PropTypes.string.isRequired
  }),
  chartConfig: PropTypes.shape({
    colorAxis: PropTypes.object,
    colorByLabel: PropTypes.object,
    colors: PropTypes.arrayOf(PropTypes.string)
  }),
  chartType: PropTypes.oneOf([
    'stackedBar',
    'stackedColumn',
    'semiCircle',
    'pie',
    'genderAgeColumn',
    'DOOHGenderAgeColumn',
    'treemap'
  ]),
  country: PropTypes.string,
  currencyInfo: PropTypes.exact({
    baseCurrency: PropTypes.oneOf(Object.keys(BaseCurrencyEnum)),
    countries: PropTypes.arrayOf(PropTypes.string),
    currencyPosition: PropTypes.oneOf(Object.keys(BaseCurrencyPositionEnum)),
    currencySymbol: PropTypes.oneOf(Object.keys(BaseCurrencySymbolEnum))
  }),
  family: PropTypes.string,
  field: PropTypes.string.isRequired,
  filterOptions: PropTypes.shape({
    attrType: PropTypes.arrayOf(PropTypes.string)
  }),
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      props: PropTypes.any,
      type: PropTypes.oneOf([
        'selector',
        'checkbox',
        'boundary-shape',
        'content-category-provider',
        'dco-dimension-selector'
      ])
    })
  ),
  groupsOptions: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.number,
      groupBy: PropTypes.string.isRequired,
      others: PropTypes.bool,
      size: PropTypes.number
    })
  ),
  hideKpi: PropTypes.bool,
  hideOthers: PropTypes.bool,
  id: PropTypes.string.isRequired,
  insightsDataBeingChecked: PropTypes.bool,
  insightsDataCanBeLoaded: PropTypes.bool,
  insightsDataTotal: PropTypes.number,
  itemId: PropTypes.string.isRequired,
  mapId: PropTypes.string,
  metricsKpis: PropTypes.shape({
    kpi: PropTypes.object,
    metric: PropTypes.object
  }),
  onConfigChange: PropTypes.func,
  onFiltersChange: PropTypes.func,
  onRankingItemClick: PropTypes.func,
  rankingConfig: PropTypes.shape({
    colorByLabel: PropTypes.object,
    colors: PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.string),
      others: PropTypes.string.isRequired
    }),
    flexDirection: PropTypes.oneOf(['row', 'column']),
    labelPosition: PropTypes.oneOf(['left', 'right']),
    showProgressBar: PropTypes.bool.isRequired,
    variant: PropTypes.string
  }),
  rawDataMode: PropTypes.shape({
    defaultActive: PropTypes.bool,
    disabled: PropTypes.bool,
    force: PropTypes.bool,
    hideCheckbox: PropTypes.bool,
    onChange: PropTypes.func
  }),
  reportOptions: PropTypes.object,
  section: PropTypes.string.isRequired,
  showMoreQuantity: PropTypes.number,
  shownItems: PropTypes.number
};

export default React.memo(ReportingInsightsRankingItem);
